import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import ExploreIndex from '../components/directory/ExploreIndex';
import GlobalSearch from '../components/directory/GlobalSearch';
import SiteHeader from '../components/directory/SiteHeader';
import PageContainer from '../components/layout/PageContainer';
import { POPULAR_DESTINATIONS } from '../config/popularDestinations';
import { getDirectoryCountries } from '../services/dataStore';
import { useLocale } from '../context/LocaleContext';
import { isSupportedLocale } from '../utils/lang-route';

const HomePage: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const { locale, setLocale } = useLocale();
  const effectiveLocale = isSupportedLocale(lang) ? lang : locale;
  const isEn = effectiveLocale === 'en';

  React.useEffect(() => {
    if (isSupportedLocale(lang) && lang !== locale) {
      setLocale(lang);
    }
  }, [lang, locale, setLocale]);

  const countries = useMemo(
    () => getDirectoryCountries(effectiveLocale, 'Europe'),
    [effectiveLocale]
  );

  const heroTitle = isEn
    ? "Europe's official attraction links, in one place."
    : '欧洲景点官网，一站直达。';

  const heroSubtitle = isEn
    ? 'Find official attraction websites and ticket links across Europe — no resellers, no endless Google searching.'
    : '直达欧洲景点官网与购票入口，非 OTA、非代理，少绕 Google 弯路。';

  return (
    <PageContainer>
      <SiteHeader />

      {/* Hero：标题 + 副标题 */}
      <section className="mt-12 max-w-2xl">
        <h1 className="text-[2rem] leading-[1.25] font-semibold tracking-tight text-[var(--dir-text)]">
          {heroTitle}
        </h1>
        <p className="mt-2.5 text-base text-[var(--dir-muted)] leading-relaxed">{heroSubtitle}</p>
      </section>

      {/* 搜索 + Popular */}
      <section className="mt-8">
        <GlobalSearch autoFocus={false} />
        <div className="mt-3.5 flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
          <span className="text-[var(--dir-muted)] mr-1">{isEn ? 'Popular' : '热门'}</span>
          {POPULAR_DESTINATIONS.map((dest) => (
            <Link
              key={dest.citySlug + dest.countryId}
              to={`/${effectiveLocale}/country/${dest.countryId}#city-${dest.citySlug}`}
              className="inline-flex items-center px-3 py-1 rounded-md border border-[var(--dir-border)] bg-[var(--dir-bg)] text-[var(--dir-text)] hover:border-[var(--dir-coral)] hover:text-[var(--dir-coral)] transition-colors"
            >
              {dest.label}
            </Link>
          ))}
        </div>
      </section>

      <hr className="dir-divider mt-12 mb-8" />

      <h2 className="text-sm font-semibold text-[var(--dir-muted)] uppercase tracking-wide mb-6">
        {isEn ? 'Explore Europe' : '探索欧洲'}
      </h2>
      <ExploreIndex countries={countries} />

      <footer className="mt-16 pt-8 border-t border-[var(--dir-border)] text-center text-sm text-[var(--dir-muted)]">
        <p>{isEn ? 'Skip the resellers. Go official.' : '跳过代理，直达官网。'}</p>
        <p className="mt-2 text-xs">
          {isEn ? 'Report broken links:' : '链接问题请联系：'}
          <a href="mailto:reneewu86@outlook.com" className="dir-link-coral ml-1">
            reneewu86@outlook.com
          </a>
        </p>
      </footer>
    </PageContainer>
  );
};

export default HomePage;
